import { all, put, takeLatest } from 'redux-saga/effects'
import {
  NOTIFICATIONS,
  NOTIFICATIONS_COUNT,
  getDiscourseUserNotifications,
  DISCOURSE_CATEGORIES,
  getDiscourseUserCategories,
  updateDiscourseUserCategories,
  markAllUsersNotificationsAsRead,
  getUnreadNotificationsCount,
} from 'core/actions/NotificationActions'
import { API, Auth } from 'aws-amplify'
import {
  getUsersUnreadNotificationsCount,
  listUsersNotifications,
  usersDiscourseCategories,
} from 'api/graphql/queries'
import { markNotificationsAsRead, updateUserCategoriesWatchStatus } from 'api/graphql/mutations'
import { stringify } from 'api/graphql/utils'
import * as Alert from 'utils/Alerts'
import i18n from 'utils/i18n'

export function* fetchNotificationsSaga({ payload: { userId, offset, limit, append } }) {
  try {
    const user = yield Auth.currentAuthenticatedUser()
    const cognitoId = user?.attributes?.sub
    const requestInfo = {
      queryStringParameters: {
        query: listUsersNotifications(userId, cognitoId, limit, offset),
      },
    }

    const { data } = yield API.post('NotificationsAPI', '/graphql?query', requestInfo)

    if (data?.errors) {
      throw new Error('Error: Notifications not fetched')
    }
    yield put(getDiscourseUserNotifications.success({ ...data?.paginatedUsersNotifications, append }))
  } catch (e: any) {
    yield put(getDiscourseUserNotifications.failure(e))
  }
}

export function* fetchUnreadNotificationsCountSaga({ payload: { userId } }) {
  try {
    const user = yield Auth.currentAuthenticatedUser()
    const cognitoId = user?.attributes?.sub
    const requestInfo = {
      queryStringParameters: {
        query: getUsersUnreadNotificationsCount(userId, cognitoId),
      },
    }

    const { data } = yield API.post('NotificationsAPI', '/graphql?query', requestInfo)

    if (data?.errors) {
      throw new Error('Error: Notifications count not fetched')
    }

    yield put(getUnreadNotificationsCount.success(data?.usersUnreadNotificationsCount || 0))
  } catch (e: any) {
    yield put(getUnreadNotificationsCount.failure(e))
  }
}

export function* markAllUsersNotificationsAsReadSaga({ payload: { userId } }) {
  try {
    const user = yield Auth.currentAuthenticatedUser()
    const cognitoId = user?.attributes?.sub
    const requestInfo = {
      queryStringParameters: {
        query: markNotificationsAsRead(userId, cognitoId),
      },
    }

    const { data } = yield API.post('NotificationsAPI', '/graphql?query', requestInfo)

    if (data?.errors) {
      throw new Error('Error: could not mark notfications as read')
    }

    yield put(markAllUsersNotificationsAsRead.success())
  } catch (e: any) {
    yield put(markAllUsersNotificationsAsRead.failure(e))
  }
}

export function* fetchDiscourseCategoriesSaga({ payload: { userId } }) {
  const id = Number(userId)
  try {
    const user = yield Auth.currentAuthenticatedUser()
    const cognitoId = user?.attributes?.sub
    const requestInfo = {
      queryStringParameters: {
        query: usersDiscourseCategories(id, cognitoId),
      },
    }
    const { data } = yield API.post('NotificationsAPI', '/graphql?query', requestInfo)

    if (data?.errors) {
      throw new Error('Error: Fetch discourse categories failed.')
    }

    yield put(getDiscourseUserCategories.success(data?.usersDiscourseCategories))
  } catch (e: any) {
    yield put(getDiscourseUserCategories.failure(e))
    yield Alert.setErrorAlert(i18n.t('api.errors.getDiscourseCategories'))
  }
}

export function* updateDiscourseCategoriesSaga({ payload: { categories, discourseUsername } }) {
  const stringifyCategories = stringify(categories)
  try {
    const user = yield Auth.currentAuthenticatedUser()
    const cognitoId = user?.attributes?.sub
    const requestInfo = {
      queryStringParameters: {
        query: updateUserCategoriesWatchStatus(discourseUsername, stringifyCategories, cognitoId),
      },
    }
    const { data } = yield API.post('NotificationsAPI', '/graphql?query', requestInfo)

    if (data?.errors) {
      throw new Error('Error: Update discourse categories failed.')
    }

    yield put(updateDiscourseUserCategories.success(categories))
    yield Alert.setSuccessAlert(i18n.t('api.success.updateDiscourseCategories'))
  } catch (e: any) {
    yield put(updateDiscourseUserCategories.failure(e))
    yield Alert.setErrorAlert(i18n.t('api.errors.updateDiscourseCategories'))
  }
}

function* notificationsSaga() {
  yield all([
    takeLatest(NOTIFICATIONS.GET.REQUEST, fetchNotificationsSaga),
    takeLatest(NOTIFICATIONS_COUNT.GET.REQUEST, fetchUnreadNotificationsCountSaga),
    takeLatest(NOTIFICATIONS.POST.REQUEST, markAllUsersNotificationsAsReadSaga),
    takeLatest(DISCOURSE_CATEGORIES.GET.REQUEST, fetchDiscourseCategoriesSaga),
    takeLatest(DISCOURSE_CATEGORIES.POST.REQUEST, updateDiscourseCategoriesSaga),
  ])
}

export default notificationsSaga
